import {gql} from "@apollo/client";
import {useLazyQuery$} from "../../../graphqlsupport";
import {
    ClientReportAddendumRequestReasonInput,
    ClientReportSubmitClientOfferHourInput,
    ClientReportSubmitClinicalRecommendationHourInput,
    ClientReportSubmitTreatmentOfferedInput, ReportServiceType, ShortAddendumRequestReason
} from "../domain";
import {useEffect, useRef} from "react";
import {DeepPartial, UnpackNestedValue, UseFormReturn} from "react-hook-form";
import {getFieldsToResetForHourVisibility, getHourVisibility, ResetFieldOptions} from "../form-components/auth-hours/resetHiddenFields";
import {SPLIT_DELIMITER} from "placement/const";
import {isEqual} from "lodash";


export type LatestOfferedHourData =
    {
        // for reusing clinical recommendation and offer hours input
        reportType: string;
        serviceType: string[];
    }
    & ClientReportSubmitClinicalRecommendationHourInput
    & ClientReportSubmitClientOfferHourInput
    & ClientReportSubmitTreatmentOfferedInput
    & ClientReportAddendumRequestReasonInput;

export function filterServiceTypesToAvailableOptions(serviceTypes: string[], availableOptions?: Map<string, string> | null): string[] {
    if (availableOptions == null || availableOptions.has(serviceTypes.join(SPLIT_DELIMITER))) {
        return serviceTypes;
    }
    return [];
}

export function pickHoursForServiceTypes<T extends Record<string, unknown>>(hours: T, serviceTypes: ReportServiceType[], options: ResetFieldOptions): T {
    const filteredHours = {...hours};
    getFieldsToResetForHourVisibility(getHourVisibility(serviceTypes), options)
        .forEach((field) => filteredHours[field as keyof T] = null as T[keyof T]);
    return filteredHours;
}

const AUTHORIZATION_LATEST_OFFERED_HOURS = gql`
query authorizationLatestOfferedHours($authorizationGuid: UUID) {
    authorizationLatestOfferedHours(authorizationGuid: $authorizationGuid) {
        reportType
        serviceType
        addendumRequestReason
        
        caregiverMidLevelHoursMin
        caregiverMidLevelHoursMax
        caregiverHighLevelHoursMin
        caregiverHighLevelHoursMax
        practitionerParaprofessionalHoursMin
        practitionerParaprofessionalHoursMax
        practitionerMidLevelHoursMin
        practitionerMidLevelHoursMax
        practitionerHighLevelHoursMin
        practitionerHighLevelHoursMax
        practitionerGroupCareHoursMin
        practitionerGroupCareHoursMax
        offeredParaprofessionalHours
        offeredMidLevelHours
        offeredHighLevelHours
        offeredGroupCareHours
        currentOfferedParaprofessionalHours
        currentOfferedMidLevelHours
        currentOfferedHighLevelHours
        currentOfferedGroupCareHours
    }
}
`;

function useLatestOfferedHoursQuery$(authorizationGuid?: string) {
    const {execute, data, isRunning$, error} = useLazyQuery$<{
        authorizationGuid?: string
    }, {
        authorizationLatestOfferedHours: LatestOfferedHourData
    }>(AUTHORIZATION_LATEST_OFFERED_HOURS, authorizationGuid == null ? {} : {
        authorizationGuid,
    });

    return {
        execute,
        data,
        isRunning$,
        error
    };
}

export function usePopulateLatestOfferedHours<T>(latestHours: LatestOfferedHourData | null | undefined, methods: UseFormReturn<T>, serviceTypes: ReportServiceType[]) {
    const {getValues, reset, setValue} = methods;
    const latestHoursKey = JSON.stringify(latestHours);
    const serviceTypesKey = JSON.stringify(serviceTypes);

    const getValuesRef = useRef(getValues);
    const resetRef = useRef(reset);
    const setValueRef = useRef(setValue);
    getValuesRef.current = getValues;
    resetRef.current = reset;
    setValueRef.current = setValue;

    const lastLatestHoursRef = useRef<LatestOfferedHourData | null | undefined>(undefined);
    const lastTargetServiceTypesRef = useRef<ReportServiceType[] | undefined>(undefined);
    const applyKey = `${latestHoursKey}|${serviceTypesKey}`;

    useEffect(() => {
        if (latestHours == null) {
            return;
        }

        const latestHoursChanged = !isEqual(latestHours, lastLatestHoursRef.current);
        const serviceTypesChanged = !isEqual(serviceTypes, lastTargetServiceTypesRef.current);

        if (!latestHoursChanged && !serviceTypesChanged) {
            return;
        }

        // On the initial latest-hours load, seed the form with the API's service type only
        // if the user has not already selected one. After that, follow the user's current
        // selection (which may be empty when they clear it).
        const targetServiceTypes = latestHoursChanged && serviceTypes.length === 0
            ? ((latestHours.serviceType as ReportServiceType[]) ?? [])
            : serviceTypes;

        lastLatestHoursRef.current = latestHours;
        lastTargetServiceTypesRef.current = targetServiceTypes;

        const getValues = getValuesRef.current;
        const reset = resetRef.current;
        // @ts-ignore
        const reportType = getValues('reportType') || null;

        const offerFields = pickHoursForServiceTypes({
            offeredParaprofessionalHours: latestHours.offeredParaprofessionalHours,
            offeredMidLevelHours: latestHours.offeredMidLevelHours,
            offeredHighLevelHours: latestHours.offeredHighLevelHours,
            offeredGroupCareHours: latestHours.offeredGroupCareHours,
        }, targetServiceTypes, {offeredHours: true});

        const resetValues = {
            // this is IMPORTANT to avoid losing important default values
            ...getValues(),

            serviceType: targetServiceTypes,

            addendumRequestReason: filterAddendumRequestReason(latestHours.addendumRequestReason ?? [], reportType ?? ''),

            currentOfferedParaprofessionalHours: null,
            currentOfferedMidLevelHours: null,
            currentOfferedHighLevelHours: null,
            currentOfferedGroupCareHours: null,

            // BHPNPP-1867 some workflows will set a default report type
            // should not override this default
            reportType: reportType ?? latestHours.reportType,

            ...(offerFields as UnpackNestedValue<DeepPartial<T>>),
        } as UnpackNestedValue<DeepPartial<T>>;

        const setValue = setValueRef.current;

        // When the latest API data changes, update the reference/default state so that
        // closing the modal rewinds to that latest pre-populated snapshot.  When only
        // the user selection changed, apply the new values without overwriting the
        // reference state.
        if (latestHoursChanged) {
            reset(resetValues);
        } else {
            setValue('serviceType' as any, targetServiceTypes as any);
            setValue('addendumRequestReason' as any, filterAddendumRequestReason(latestHours.addendumRequestReason ?? [], reportType ?? '') as any);
            setValue('reportType' as any, (reportType ?? latestHours.reportType) as any);
            setValue('currentOfferedParaprofessionalHours' as any, null as any);
            setValue('currentOfferedMidLevelHours' as any, null as any);
            setValue('currentOfferedHighLevelHours' as any, null as any);
            setValue('currentOfferedGroupCareHours' as any, null as any);
            Object.entries(offerFields).forEach(([field, value]) => {
                setValue(field as any, value as any);
            });
        }
    }, [applyKey]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useLatestOfferedHours<T>(showModal: boolean, methods: UseFormReturn<T>, authorizationGuid?: string, serviceTypes: ReportServiceType[] = []) {
    const {execute, data, isRunning$, error,} = useLatestOfferedHoursQuery$();
    useEffect(() => {
        if (showModal && authorizationGuid != null) {
            execute({
                authorizationGuid,
            });
        }
    }, [authorizationGuid, showModal, execute]);
    usePopulateLatestOfferedHours(data?.authorizationLatestOfferedHours, methods, serviceTypes);
    return {
        data,
        isRunning$,
        error,
    };
}

const filterAddendumRequestReason = (reasons: string[], reportType: string) => {
    if (reportType === 'ABA_SHORT_ADDENDUM') {
        reasons = reasons.filter((r) => Object.keys(ShortAddendumRequestReason).includes(r));
    }

    return reasons;
}
